import { randomInt } from "./Random";
import { PinPath, Point, ScoringFunction } from "./Types";
import { IAlgorithm } from "./IAlgorithm";

export class MonteCarloDescentAlgorithm implements IAlgorithm {
    private points: Point[] = [];
    private readonly scoringFunction: ScoringFunction;
    private readonly maxPathLength: number;
    private readonly maxExitDelta = 5;

    constructor(scoringFunction: ScoringFunction, maxPathLength: number = 1000) {
        this.scoringFunction = scoringFunction;
        this.maxPathLength = maxPathLength;
    }

    public init(points: Point[]): void {
        this.points = points;
    }

    public iterate(path: PinPath, diff: number): [PinPath, number] {
        return this.step(path, diff);
    }

    private randomNonZeroDelta(): number {
        const abs = randomInt(this.maxExitDelta) + 1;
        return Math.random() < 0.5 ? -abs : abs;
    }

    private step(path: PinPath, diff: number): [PinPath, number] {
        const mutationRoll = Math.random();
        let newPath: PinPath;
        const pinIndices = [...path.pinIndices];
        const exitDeltas = [...path.exitDeltas];
        
        if (mutationRoll < 0.02 && pinIndices.length > 1) {
            // 2% chance: Remove random segment
            const removeIndex = randomInt(pinIndices.length);
            pinIndices.splice(removeIndex, 1);
            exitDeltas.length = Math.max(0, pinIndices.length - 1);
            for (let i = 0; i < exitDeltas.length; i++) {
                if (exitDeltas[i] === 0) {
                    exitDeltas[i] = this.randomNonZeroDelta();
                }
            }
        } else if (mutationRoll < 0.04 && pinIndices.length < this.maxPathLength) {
            // 2% chance: Insert random segment (only if length < max)
            const insertIndex = randomInt(pinIndices.length + 1);
            const randomPointIndex = randomInt(this.points.length);
            pinIndices.splice(insertIndex, 0, randomPointIndex);
            exitDeltas.splice(insertIndex, 0, this.randomNonZeroDelta());
            exitDeltas.length = Math.max(0, pinIndices.length - 1);
        } else if (mutationRoll < 0.20 && exitDeltas.length > 0) {
            // 16% chance: mutate exit delta for a random segment
            const i = randomInt(exitDeltas.length);
            exitDeltas[i] = this.randomNonZeroDelta();
        } else {
            // 80% chance: Replace random pin
            const i = randomInt(pinIndices.length);
            const randomPointIndex = randomInt(this.points.length);
            pinIndices[i] = randomPointIndex;
        }

        newPath = { pinIndices, exitDeltas };
        
        const newDiff = this.scoringFunction(newPath);
        
        return newDiff < diff ? [newPath, newDiff] : [path, diff];
    }
}


